import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Wallet, ShieldCheck, BarChart3, Mail, Lock } from 'lucide-react';
import { Payments } from './pages/Payments';
import { KYC } from './pages/KYC';
import { Reports } from './pages/Reports';
import { AccountingLayout } from './pages/accounting/AccountingLayout';
import { AccountingIndexPage } from './pages/accounting/AccountingIndexPage';
import { AccountingViewPage } from './pages/accounting/AccountingViewPage';
import { RiskManagement } from './pages/RiskManagement';
import { Notifications } from './pages/Notifications';
import { Settings } from './pages/Settings';
import { Users } from './pages/Users';
import { Analytics } from './pages/Analytics';
import { LoanProducts } from './pages/LoanProducts';
import { Collections } from './pages/Collections';
import { BranchesAndCash } from './pages/BranchesAndCash';
import { CashRegistersPage } from './pages/CashRegisters';
import { TreasuryPage } from './pages/Treasury';
import { TreasuryLayout } from './pages/treasury/TreasuryLayout';
import { MoveFundsWizard } from './pages/treasury/MoveFundsWizard';
import { TreasuryBanksPage } from './pages/treasury/TreasuryBanksPage';
import { TreasurySupervisionPage } from './pages/treasury/TreasurySupervisionPage';
import { TreasuryCashRegistersPage } from './pages/treasury/TreasuryCashRegistersPage';
import { PayrollDiscounts } from './pages/PayrollDiscounts';
import { BaproDebitBatches } from './pages/BaproDebitBatches';
import { PayrollBatchCollections } from './pages/PayrollBatchCollections';
import { LoanApplications } from './pages/LoanApplications';
import { NewLoanApplication } from './pages/NewLoanApplication';
import { CustomerCompliance } from './pages/CustomerCompliance';
import { LoanRenewals } from './pages/LoanRenewals';
import { BranchObjectives } from './pages/BranchObjectives';
import { OperationalReports } from './pages/OperationalReports';
import { LegacyReports } from './pages/LegacyReports';
import { FiscalLegal } from './pages/FiscalLegal';
import { CrmMarketing } from './pages/CrmMarketing';
import { AuditLogs } from './pages/AuditLogs';
import { Dashboard } from './pages/Dashboard';
import { CashSessionBar, CashSessionStaleBanner, useCashSession } from './components/CashSessionBar';
import { DisbursementApproveModal } from './components/DisbursementApproveModal';
import { CompleteBankDisbursementModal } from './components/CompleteBankDisbursementModal';
import { TenantContextBar } from './components/tenant/TenantContextBar';
import { CustomerDetail } from './pages/CustomerDetail';
import { LoanDetail } from './pages/LoanDetail';
import { ReferenceData } from './pages/ReferenceData';
import { BankReconciliation } from './pages/BankReconciliation';
import { PayablesPage } from './pages/Payables';
import { Forbidden } from './pages/Forbidden';
import { apiService } from './services/api.service';
import { PermissionsProvider, usePermissions } from './context/PermissionsContext';
import { ProtectedRoute, PermissionGate } from './components/ProtectedRoute';
import { HomeRedirect } from './components/HomeRedirect';
import { FEATURES } from './config/features';
import { AppSidebar, SIDEBAR_ICON_COLLAPSE_KEY } from './components/AppSidebar';
import { useTenantStore } from './stores/tenant.store';

// Componente de Login
function Login() {
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      await apiService.login(credentials.email, credentials.password);
      window.location.href = '/';
    } catch (apiError: any) {
      setError(apiError?.response?.data?.message ?? 'Credenciales incorrectas');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Lado izquierdo: marca CreditNow */}
      <div
        className="hidden lg:flex lg:w-1/2 text-white p-12 flex-col justify-center relative overflow-hidden"
        style={{ background: 'linear-gradient(145deg, #3C1D8C 0%, #26106B 48%, #1B0D45 100%)' }}
      >
        <div
          className="absolute -top-20 -right-16 w-[28rem] h-[28rem] rounded-full blur-3xl opacity-40"
          style={{ background: 'radial-gradient(circle, #FF7A00 0%, transparent 70%)' }}
        />
        <div
          className="absolute -bottom-28 -left-20 w-96 h-96 rounded-full blur-3xl opacity-30"
          style={{ background: 'radial-gradient(circle, #FFD24D 0%, transparent 65%)' }}
        />

        <div className="relative z-10 max-w-lg">
          <p
            className="inline-flex items-center rounded-full px-3 py-1 text-[11px] font-semibold tracking-wide uppercase mb-5"
            style={{ backgroundColor: 'rgba(255,122,0,0.2)', color: '#FFD24D' }}
          >
            Panel operativo
          </p>
          <h1 className="text-5xl font-extrabold tracking-tight leading-tight">
            Admin <span style={{ color: '#FF7A00' }}>creditnow</span>
          </h1>
          <p className="mt-6 text-lg leading-relaxed" style={{ color: 'rgba(255,247,235,0.88)' }}>
            El centro de operación de CreditNow: originá créditos, cobrá en sucursal, controlá caja
            y seguí la cartera de la Patagonia en un solo lugar.
          </p>

          <ul className="mt-10 space-y-4" style={{ color: 'rgba(255,247,235,0.92)' }}>
            <li className="flex items-center gap-3">
              <span
                className="flex items-center justify-center w-8 h-8 rounded-lg"
                style={{ backgroundColor: 'rgba(255,122,0,0.25)' }}
              >
                <ShieldCheck className="w-4 h-4" style={{ color: '#FFD24D' }} />
              </span>
              <span className="text-sm">Presolicitudes, validaciones y alta de clientes</span>
            </li>
            <li className="flex items-center gap-3">
              <span
                className="flex items-center justify-center w-8 h-8 rounded-lg"
                style={{ backgroundColor: 'rgba(255,122,0,0.25)' }}
              >
                <Wallet className="w-4 h-4" style={{ color: '#FFD24D' }} />
              </span>
              <span className="text-sm">Pagos, cobranza y caja de cada sucursal</span>
            </li>
            <li className="flex items-center gap-3">
              <span
                className="flex items-center justify-center w-8 h-8 rounded-lg"
                style={{ backgroundColor: 'rgba(255,122,0,0.25)' }}
              >
                <BarChart3 className="w-4 h-4" style={{ color: '#FFD24D' }} />
              </span>
              <span className="text-sm">Tablero, mora y reportes operativos</span>
            </li>
          </ul>
        </div>
      </div>

      {/* Lado derecho: logo + formulario */}
      <div className="flex-1 lg:w-1/2 flex items-center justify-center p-6 sm:p-12" style={{ backgroundColor: '#FFF7EB' }}>
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-10">
            <div
              className="flex items-center justify-center w-12 h-12 rounded-xl shadow-lg"
              style={{
                background: 'linear-gradient(135deg, #FF7A00 0%, #FFA31A 100%)',
                boxShadow: '0 10px 24px rgba(255,122,0,0.28)',
              }}
            >
              <Wallet className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-gray-900">
              Admin <span style={{ color: '#3C1D8C' }}>creditnow</span>
            </span>
          </div>

          <h2 className="text-3xl font-bold text-gray-900">Iniciar sesión</h2>
          <p className="mt-2 text-sm text-gray-600">
            Accedé con tu usuario de operador, cajero o administración.
          </p>

          <form className="mt-8 space-y-5" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                Email
              </label>
              <div className="relative">
                <span className="pointer-events-none absolute inset-y-0 left-0 pl-3 flex items-center">
                  <Mail className="w-4 h-4 text-gray-400" />
                </span>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="appearance-none block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg placeholder-gray-400 text-gray-900 bg-white shadow-sm focus:outline-none focus:ring-2 focus:border-transparent sm:text-sm"
                  style={{ ['--tw-ring-color' as string]: '#FF7A00' }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#FF7A00';
                    e.currentTarget.style.boxShadow = '0 0 0 2px rgba(255,122,0,0.35)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '';
                    e.currentTarget.style.boxShadow = '';
                  }}
                  placeholder="operador@creditnow.com.ar"
                  value={credentials.email}
                  onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1.5">
                Contraseña
              </label>
              <div className="relative">
                <span className="pointer-events-none absolute inset-y-0 left-0 pl-3 flex items-center">
                  <Lock className="w-4 h-4 text-gray-400" />
                </span>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  className="appearance-none block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg placeholder-gray-400 text-gray-900 bg-white shadow-sm focus:outline-none focus:ring-2 focus:border-transparent sm:text-sm"
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#FF7A00';
                    e.currentTarget.style.boxShadow = '0 0 0 2px rgba(255,122,0,0.35)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '';
                    e.currentTarget.style.boxShadow = '';
                  }}
                  placeholder="••••••••"
                  value={credentials.password}
                  onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                />
              </div>
            </div>

            {error && (
              <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center items-center py-2.5 px-4 rounded-full text-sm font-semibold text-white focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed transition-opacity hover:opacity-95"
              style={{
                background: 'linear-gradient(90deg, #FF7A00 0%, #FFA31A 100%)',
                boxShadow: '0 8px 20px rgba(255,122,0,0.35)',
              }}
            >
              {isLoading ? 'Iniciando sesión…' : 'Iniciar sesión'}
            </button>
          </form>

          <p className="mt-8 text-xs text-center text-gray-500">
            © {new Date().getFullYear()} CreditNow · Admin. Todos los derechos reservados.
          </p>
        </div>
      </div>
    </div>
  );
}

// Componente de Clientes
function Customers() {
  const navigate = useNavigate();
  const tenantBranchId = useTenantStore((s: { branchId: string | null }) => s.branchId);
  const [customers, setCustomers] = useState<any[]>([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, pageSize: 25, pageCount: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [tempPassword, setTempPassword] = useState<{ email: string; pwd: string } | null>(null);
  const [branches, setBranches] = useState<any[]>([]);
  const [filters, setFilters] = useState({
    q: '',
    branchId: '',
    status: '',
    page: 1,
    pageSize: 25,
  });
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    birthDate: '',
    gender: 'M' as 'M' | 'F' | 'X',
    phone: '',
    monthlyIncome: '',
    documentNumber: '',
    branchId: '',
  });

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.listCustomers({
        page: filters.page,
        pageSize: filters.pageSize,
        q: filters.q.trim() || undefined,
        branchId: filters.branchId || undefined,
        status: filters.status || undefined,
      });
      setCustomers(res.data);
      setMeta(res.meta);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    void apiService.getBranches(true).then(setBranches).catch(() => setBranches([]));
  }, []);

  React.useEffect(() => {
    if (tenantBranchId && !form.branchId) {
      setForm((f) => ({ ...f, branchId: tenantBranchId }));
    }
  }, [tenantBranchId]);

  React.useEffect(() => {
    const t = setTimeout(() => {
      void load();
    }, filters.q ? 300 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.page, filters.pageSize, filters.branchId, filters.status, filters.q]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setError(null);
    try {
      const created: any = await apiService.createCustomer({
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        birthDate: form.birthDate,
        gender: form.gender,
        phone: form.phone || undefined,
        monthlyIncome: form.monthlyIncome ? parseFloat(form.monthlyIncome) : undefined,
        documentNumber: form.documentNumber || undefined,
        branchId: form.branchId || undefined,
      });
      if (created.tempPassword) {
        setTempPassword({ email: form.email, pwd: created.tempPassword });
      }
      setShowForm(false);
      setForm({
        firstName: '',
        lastName: '',
        email: '',
        birthDate: '',
        gender: 'M',
        phone: '',
        monthlyIncome: '',
        documentNumber: '',
        branchId: tenantBranchId || '',
      });
      setFilters((f) => ({ ...f, page: 1 }));
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setCreating(false);
    }
  };

  const badge = (txt: string | null | undefined, palette: Record<string, string>) =>
    `inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${palette[txt ?? ''] ?? 'bg-gray-100 text-gray-800'}`;

  const branchLabel = (c: any) => {
    const b = c.user?.branch;
    if (!b) return '—';
    return `${b.code} — ${b.name}`;
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Clientes</h1>
          <p className="text-sm text-gray-500 mt-1">{meta.total.toLocaleString('es-AR')} en total</p>
        </div>
        <button
          onClick={() => setShowForm((v) => !v)}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          {showForm ? 'Cancelar' : '+ Nuevo cliente'}
        </button>
      </div>

      {tempPassword && (
        <div className="mb-6 p-4 rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-sm">
          <p className="font-semibold mb-1">Credenciales temporales generadas</p>
          <p>
            Email: <code className="font-mono">{tempPassword.email}</code> · Password:{' '}
            <code className="font-mono">{tempPassword.pwd}</code>
          </p>
          <p className="mt-1 text-xs">Comunicáselas al cliente. Sólo se muestran en entornos de desarrollo.</p>
          <button onClick={() => setTempPassword(null)} className="mt-2 text-xs underline">Cerrar</button>
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>
      )}

      {showForm && (
        <form onSubmit={submit} className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200 p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
            <input required value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md" /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Apellido</label>
            <input required value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md" /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md" /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Teléfono</label>
            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md" /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">DNI</label>
            <input value={form.documentNumber} onChange={(e) => setForm({ ...form, documentNumber: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md" /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Sucursal</label>
            <select required value={form.branchId} onChange={(e) => setForm({ ...form, branchId: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md">
              <option value="">Seleccionar sucursal</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>{b.code} — {b.name}</option>
              ))}
            </select>
          </div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Fecha de nacimiento</label>
            <input required type="date" value={form.birthDate} onChange={(e) => setForm({ ...form, birthDate: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md" /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Género</label>
            <select value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value as any })} className="w-full px-3 py-2 border border-gray-300 rounded-md">
              <option value="M">Masculino</option>
              <option value="F">Femenino</option>
              <option value="X">Otro</option>
            </select>
          </div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Ingreso mensual</label>
            <input type="number" step="0.01" value={form.monthlyIncome} onChange={(e) => setForm({ ...form, monthlyIncome: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md" /></div>
          <div className="md:col-span-2 flex justify-end gap-2">
            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md">Cancelar</button>
            <button type="submit" disabled={creating} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
              {creating ? 'Creando…' : 'Crear cliente'}
            </button>
          </div>
        </form>
      )}

      <div className="mb-4 bg-white rounded-lg border border-gray-200 p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        <input
          value={filters.q}
          onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value, page: 1 }))}
          placeholder="Buscar nombre, DNI, email, teléfono…"
          className="px-3 py-2 border border-gray-300 rounded-md md:col-span-2"
        />
        <select
          value={filters.branchId}
          onChange={(e) => setFilters((f) => ({ ...f, branchId: e.target.value, page: 1 }))}
          className="px-3 py-2 border border-gray-300 rounded-md"
        >
          <option value="">Todas las sucursales</option>
          {branches.map((b) => (
            <option key={b.id} value={b.id}>{b.code} — {b.name}</option>
          ))}
        </select>
        <select
          value={filters.status}
          onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value, page: 1 }))}
          className="px-3 py-2 border border-gray-300 rounded-md"
        >
          <option value="">Todos los estados</option>
          <option value="ACTIVE">Activo</option>
          <option value="INACTIVE">Inactivo</option>
          <option value="SUSPENDED">Suspendido</option>
        </select>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-gray-500">Cargando…</div>
        ) : customers.length === 0 ? (
          <div className="p-12 text-center text-gray-500">No hay clientes con esos filtros.</div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contacto</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sucursal</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Riesgo</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {customers.map((c) => (
                <tr key={c.id} onClick={() => navigate(`/customers/${c.id}`)} className="hover:bg-gray-50 cursor-pointer">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-blue-700 hover:underline">{c.firstName} {c.lastName}</div>
                    <div className="text-xs text-gray-500">{c.documentNumber ?? c.id.slice(0, 8)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{c.email}</div>
                    <div className="text-sm text-gray-500">{c.phone ?? '-'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{branchLabel(c)}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={badge(c.riskLevel, { LOW: 'bg-green-100 text-green-800', MEDIUM: 'bg-yellow-100 text-yellow-800', HIGH: 'bg-red-100 text-red-800' })}>
                      {c.riskLevel ?? '—'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={badge(c.status, { ACTIVE: 'bg-green-100 text-green-800', INACTIVE: 'bg-yellow-100 text-yellow-800', SUSPENDED: 'bg-red-100 text-red-800' })}>
                      {c.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-t bg-gray-50 text-sm">
          <div className="text-gray-600">
            Página {meta.page} de {meta.pageCount} · {meta.total.toLocaleString('es-AR')} clientes
          </div>
          <div className="flex items-center gap-2">
            <select
              value={filters.pageSize}
              onChange={(e) => setFilters((f) => ({ ...f, pageSize: Number(e.target.value), page: 1 }))}
              className="px-2 py-1 border rounded-md"
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <button
              type="button"
              disabled={filters.page <= 1 || loading}
              onClick={() => setFilters((f) => ({ ...f, page: f.page - 1 }))}
              className="px-3 py-1 border rounded-md disabled:opacity-40"
            >
              Anterior
            </button>
            <button
              type="button"
              disabled={filters.page >= meta.pageCount || loading}
              onClick={() => setFilters((f) => ({ ...f, page: f.page + 1 }))}
              className="px-3 py-1 border rounded-md disabled:opacity-40"
            >
              Siguiente
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Componente de Préstamos
const LOAN_STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pendiente',
  AWAITING_TRANSFER: 'Pendiente transferencia',
  APPROVED: 'Aprobado',
  ACTIVE: 'Activo',
  COMPLETED: 'Finalizado',
  REJECTED: 'Rechazado',
  CANCELLED: 'Anulado',
  DEFAULTED: 'Mora',
  LEGAL: 'Legal',
};

function Loans() {
  const location = useLocation();
  const navigate = useNavigate();
  const pendingOnly = location.pathname.endsWith('/pending');
  const transferPendingOnly = location.pathname.endsWith('/transfer-pending');
  const listTitle = pendingOnly
    ? 'Pendientes de desembolso'
    : transferPendingOnly
      ? 'Pendientes de transferencia'
      : 'Créditos';
  const listSubtitle = pendingOnly
    ? 'Créditos a la espera de aceptación (efectivo o marcar transferencia).'
    : transferPendingOnly
      ? 'Créditos aceptados por transferencia: elegí cuenta, subí el PDF y otorgá.'
      : 'Cartera de créditos otorgados.';
  const { sessionId: cashSessionId } = useCashSession();
  const tenantBranchId = useTenantStore((s: { branchId: string | null }) => s.branchId);
  const [loans, setLoans] = useState<any[]>([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, pageSize: 25, pageCount: 1 });
  const [customers, setCustomers] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [branches, setBranches] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [actingOn, setActingOn] = useState<string | null>(null);
  const [payoffLoanId, setPayoffLoanId] = useState<string | null>(null);
  const [payoffQuote, setPayoffQuote] = useState<any | null>(null);
  const [payoffLoading, setPayoffLoading] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<any | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [approveTarget, setApproveTarget] = useState<any | null>(null);
  const [transferTarget, setTransferTarget] = useState<any | null>(null);
  const [filters, setFilters] = useState({
    q: '',
    branchId: '',
    status: '',
    dateFrom: '',
    dateTo: '',
    page: 1,
    pageSize: 25,
  });
  const [form, setForm] = useState({
    customerId: '',
    productId: '',
    amount: '',
    termMonths: '',
    channelType: 'PRIVATE' as 'PRIVATE' | 'PUBLIC',
    publicEntityId: '',
    employeeFileNumber: '',
  });
  const [publicEntities, setPublicEntities] = useState<any[]>([]);

  const loadMasters = async () => {
    const [cs, ps, ents, br] = await Promise.all([
      apiService.getCustomers(),
      apiService.getLoanProducts(),
      apiService.listPublicEntities(true, tenantBranchId || undefined).catch(() => []),
      apiService.getBranches(true).catch(() => []),
    ]);
    setCustomers(cs);
    setProducts(ps);
    setPublicEntities(ents);
    setBranches(br);
  };

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.listLoans({
        page: filters.page,
        pageSize: filters.pageSize,
        q: filters.q.trim() || undefined,
        branchId: filters.branchId || undefined,
        status: pendingOnly
          ? 'PENDING'
          : transferPendingOnly
            ? 'AWAITING_TRANSFER'
            : filters.status || undefined,
        excludePending:
          pendingOnly || transferPendingOnly
            ? undefined
            : filters.status
              ? undefined
              : true,
        dateFrom: filters.dateFrom || undefined,
        dateTo: filters.dateTo || undefined,
      });
      setLoans(res.data);
      setMeta(res.meta);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    void loadMasters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantBranchId]);

  React.useEffect(() => {
    setFilters((f) => ({
      ...f,
      page: 1,
      status: pendingOnly ? 'PENDING' : transferPendingOnly ? 'AWAITING_TRANSFER' : '',
    }));
  }, [pendingOnly, transferPendingOnly]);

  React.useEffect(() => {
    const t = setTimeout(() => {
      void load();
    }, filters.q ? 300 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.page,
    filters.pageSize,
    filters.branchId,
    filters.status,
    filters.q,
    filters.dateFrom,
    filters.dateTo,
    pendingOnly,
    transferPendingOnly,
  ]);

  const submit = async (
    e: React.FormEvent,
    override?: { validationOverride?: boolean; validationOverrideReason?: string },
  ) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await apiService.createLoan({
        customerId: form.customerId,
        productId: form.productId,
        amount: parseFloat(form.amount),
        termMonths: parseInt(form.termMonths, 10),
        channelType: form.channelType,
        publicEntityId: form.channelType === 'PUBLIC' ? form.publicEntityId : undefined,
        employeeFileNumber: form.channelType === 'PUBLIC' ? form.employeeFileNumber : undefined,
        ...override,
      });
      setShowForm(false);
      setForm({ customerId: '', productId: '', amount: '', termMonths: '', channelType: 'PRIVATE', publicEntityId: '', employeeFileNumber: '' });
      setFilters((f) => ({ ...f, page: 1 }));
      await load();
    } catch (err: any) {
      const data = err?.response?.data;
      if (err?.response?.status === 409 && data?.requiresOverride) {
        const reason = window.prompt(
          `${data.message ?? 'Hay controles en rojo.'}\n\nMotivo para continuar (mín. 10 caracteres):`,
        );
        if (reason && reason.trim().length >= 10) {
          await submit(e, { validationOverride: true, validationOverrideReason: reason.trim() });
          return;
        }
      }
      setError(data?.message ?? err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const rejectLoan = async (id: string) => {
    setActingOn(id);
    setError(null);
    try {
      await apiService.rejectLoan(id);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setActingOn(null);
    }
  };

  const openApprove = (loan: any) => {
    setError(null);
    setApproveTarget(loan);
  };



  const statusBadge = (s: string) => {
    const v: Record<string, string> = {
      PENDING: 'bg-yellow-100 text-yellow-800',
      AWAITING_TRANSFER: 'bg-orange-100 text-orange-800',
      APPROVED: 'bg-blue-100 text-blue-800',
      ACTIVE: 'bg-green-100 text-green-800',
      COMPLETED: 'bg-gray-100 text-gray-800',
      REJECTED: 'bg-red-100 text-red-800',
      CANCELLED: 'bg-gray-100 text-gray-600',
      DEFAULTED: 'bg-red-100 text-red-800',
      LEGAL: 'bg-purple-100 text-purple-800',
    };
    return `inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${v[s] ?? 'bg-gray-100 text-gray-800'}`;
  };

  const openPayoff = async (loanId: string) => {
    setPayoffLoanId(loanId);
    setPayoffQuote(null);
    setPayoffLoading(true);
    setError(null);
    try {
      setPayoffQuote(await apiService.quoteEarlyPayoff(loanId));
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
      setPayoffLoanId(null);
    } finally {
      setPayoffLoading(false);
    }
  };

  const confirmPayoff = async () => {
    if (!payoffLoanId || !payoffQuote?.quoteId) return;
    setPayoffLoading(true);
    setError(null);
    try {
      await apiService.earlyPayoff(payoffLoanId, payoffQuote.quoteId);
      setPayoffLoanId(null);
      setPayoffQuote(null);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setPayoffLoading(false);
    }
  };

  const reversePayoff = async (loanId: string) => {
    if (!confirm('¿Revertir la cancelación anticipada?')) return;
    setActingOn(loanId);
    setError(null);
    try {
      await apiService.reverseEarlyPayoff(loanId);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setActingOn(null);
    }
  };

  const confirmCancelLoan = async () => {
    if (!cancelTarget || cancelReason.trim().length < 5) return;
    setCancelling(true);
    setError(null);
    try {
      await apiService.cancelLoan(cancelTarget.id, { reason: cancelReason.trim() });
      setCancelTarget(null);
      setCancelReason('');
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setCancelling(false);
    }
  };

  const cancellableStatuses = ['PENDING', 'AWAITING_TRANSFER', 'APPROVED', 'ACTIVE', 'DEFAULTED', 'LEGAL'];

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{listTitle}</h1>
          <p className="text-sm text-gray-500 mt-1">
            {pendingOnly || transferPendingOnly
              ? listSubtitle
              : `${meta.total.toLocaleString('es-AR')} en total`}
          </p>
        </div>
        {!pendingOnly && !transferPendingOnly && (
          <button onClick={() => setShowForm((v) => !v)} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
            {showForm ? 'Cancelar' : '+ Nuevo préstamo'}
          </button>
        )}
      </div>

      {error && <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

      {pendingOnly && (
        <div className="mb-4 p-3 rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-sm">
          Al aceptar elegí <strong>efectivo</strong> (caja abierta → crédito otorgado) o{' '}
          <strong>transferencia</strong> (pasa a pendientes de transferencia para subir el PDF).
          {!cashSessionId && (
            <span className="block mt-1">
              No hay caja abierta: si vas a desembolsar en efectivo, abrila desde la barra superior.
            </span>
          )}
        </div>
      )}

      {transferPendingOnly && (
        <div className="mb-4 p-3 rounded-md bg-orange-50 border border-orange-200 text-orange-950 text-sm">
          Completá cada ítem con <strong>cuenta bancaria</strong> + <strong>PDF</strong> del comprobante. Al
          confirmar, el crédito se otorga y desaparece de este listado.
        </div>
      )}

      {showForm && !pendingOnly && !transferPendingOnly && (
        <form onSubmit={submit} className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200 p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cliente</label>
            <select required value={form.customerId} onChange={(e) => setForm({ ...form, customerId: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md">
              <option value="">Seleccionar…</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.firstName} {c.lastName} — {c.email}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Producto</label>
            <select required value={form.productId} onChange={(e) => setForm({ ...form, productId: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md">
              <option value="">Seleccionar…</option>
              {products.map((p) => <option key={p.id} value={p.id}>{p.name} — {(p.interestRate * 100).toFixed(1)}%</option>)}
            </select>
            {products.length === 0 && <p className="text-xs text-amber-600 mt-1">No hay productos. Crealos primero con el endpoint /loan-products.</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Monto</label>
            <input required type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Plazo (meses)</label>
            <input required type="number" min={1} max={120} value={form.termMonths} onChange={(e) => setForm({ ...form, termMonths: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Canal</label>
            <select value={form.channelType} onChange={(e) => setForm({ ...form, channelType: e.target.value as 'PRIVATE' | 'PUBLIC' })} className="w-full px-3 py-2 border border-gray-300 rounded-md">
              <option value="PRIVATE">Privado</option>
              <option value="PUBLIC">Público (descuento de haberes)</option>
            </select>
          </div>
          {form.channelType === 'PUBLIC' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Entidad pública</label>
                <select required value={form.publicEntityId} onChange={(e) => setForm({ ...form, publicEntityId: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md">
                  <option value="">Seleccionar…</option>
                  {publicEntities.map((e) => <option key={e.id} value={e.id}>{e.code} — {e.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Legajo</label>
                <input required placeholder="ej. 31657/1" value={form.employeeFileNumber} onChange={(e) => setForm({ ...form, employeeFileNumber: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
              </div>
            </>
          )}
          <div className="md:col-span-2 flex justify-end gap-2">
            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md">Cancelar</button>
            <button type="submit" disabled={submitting} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
              {submitting ? 'Creando…' : 'Crear préstamo'}
            </button>
          </div>
        </form>
      )}

      <div className="mb-4 bg-white rounded-lg border border-gray-200 p-4 grid grid-cols-1 md:grid-cols-6 gap-3">
        <input
          value={filters.q}
          onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value, page: 1 }))}
          placeholder="Nº préstamo, DNI o cliente…"
          className="px-3 py-2 border border-gray-300 rounded-md md:col-span-2"
        />
        <select
          value={filters.branchId}
          onChange={(e) => setFilters((f) => ({ ...f, branchId: e.target.value, page: 1 }))}
          className="px-3 py-2 border border-gray-300 rounded-md"
        >
          <option value="">Todas las sucursales</option>
          {branches.map((b) => (
            <option key={b.id} value={b.id}>{b.code} — {b.name}</option>
          ))}
        </select>
        {!pendingOnly && (
          <select
            value={filters.status}
            onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value, page: 1 }))}
            className="px-3 py-2 border border-gray-300 rounded-md"
          >
            <option value="">Todos (sin pendientes)</option>
            <option value="APPROVED">Aprobado</option>
            <option value="ACTIVE">Activo</option>
            <option value="COMPLETED">Finalizado</option>
            <option value="DEFAULTED">Mora</option>
            <option value="LEGAL">Legal</option>
            <option value="CANCELLED">Anulado</option>
            <option value="REJECTED">Rechazado</option>
          </select>
        )}
        <input
          type="date"
          value={filters.dateFrom}
          onChange={(e) => setFilters((f) => ({ ...f, dateFrom: e.target.value, page: 1 }))}
          className="px-3 py-2 border border-gray-300 rounded-md"
          title="Otorgado desde"
          aria-label="Otorgado desde"
        />
        <input
          type="date"
          value={filters.dateTo}
          onChange={(e) => setFilters((f) => ({ ...f, dateTo: e.target.value, page: 1 }))}
          className="px-3 py-2 border border-gray-300 rounded-md"
          title="Otorgado hasta"
          aria-label="Otorgado hasta"
        />
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto">
        {loading ? (
          <div className="p-12 text-center text-gray-500">Cargando…</div>
        ) : loans.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            {pendingOnly
              ? 'No hay créditos pendientes de aprobación.'
              : transferPendingOnly
                ? 'No hay créditos pendientes de transferencia.'
                : 'No hay préstamos con esos filtros.'}
          </div>
        ) : (
          <table className="min-w-[64rem] w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nº</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sucursal</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Producto</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Otorgado</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Monto</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase sticky right-0 bg-gray-50 z-10">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loans.map((l) => (
                <tr
                  key={l.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => navigate(`/loans/${l.id}`)}
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">{l.loanNumber ?? '—'}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{l.customer?.firstName} {l.customer?.lastName}</div>
                    <div className="text-xs text-gray-500">{l.customer?.documentNumber ?? l.customer?.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{l.branch?.code ?? '—'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{l.product?.name ?? '—'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {l.disbursementDate ? new Date(l.disbursementDate).toLocaleDateString('es-AR') : '—'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    ${Number(l.amount).toLocaleString('es-AR')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={statusBadge(l.status)}>{LOAN_STATUS_LABELS[l.status] ?? l.status}</span>
                  </td>
                  <td
                    className="px-4 py-4 text-right text-sm sticky right-0 bg-white z-10 shadow-[-6px_0_8px_-6px_rgba(0,0,0,0.12)]"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="inline-flex flex-col items-end gap-1 min-w-[10rem]">
                    <button
                      type="button"
                      onClick={() => navigate(`/loans/${l.id}`)}
                      className="text-slate-700 hover:text-slate-900"
                    >
                      Ver detalle
                    </button>
                    {cancellableStatuses.includes(l.status) && (
                      l.status === 'PENDING' || l.status === 'AWAITING_TRANSFER' ? (
                        <PermissionGate module="loans" action="write">
                          <button
                            disabled={actingOn === l.id || cancelling}
                            onClick={() => { setCancelTarget(l); setCancelReason(''); }}
                            className="text-red-700 hover:text-red-900 disabled:opacity-50">
                            Anular
                          </button>
                        </PermissionGate>
                      ) : (
                        <PermissionGate criticalPoint="disburse_loan">
                          <button
                            disabled={actingOn === l.id || cancelling}
                            onClick={() => { setCancelTarget(l); setCancelReason(''); }}
                            className="text-red-700 hover:text-red-900 disabled:opacity-50">
                            Anular
                          </button>
                        </PermissionGate>
                      )
                    )}
                    {l.status === 'PENDING' && (
                      <>
                        <PermissionGate criticalPoint="disburse_loan">
                          <button
                            disabled={actingOn === l.id}
                            onClick={() => openApprove(l)}
                            className="text-green-700 hover:text-green-900 disabled:opacity-50">
                            Aceptar
                          </button>
                        </PermissionGate>
                        <PermissionGate module="loans" action="write">
                          <button
                            disabled={actingOn === l.id}
                            onClick={() => rejectLoan(l.id)}
                            className="text-red-700 hover:text-red-900 disabled:opacity-50">
                            Rechazar
                          </button>
                        </PermissionGate>
                      </>
                    )}
                    {l.status === 'AWAITING_TRANSFER' && (
                      <>
                        <PermissionGate criticalPoint="complete_bank_disbursement">
                          <button
                            disabled={actingOn === l.id}
                            onClick={() => setTransferTarget(l)}
                            className="text-green-700 hover:text-green-900 font-medium disabled:opacity-50">
                            Completar transferencia
                          </button>
                        </PermissionGate>
                        <PermissionGate module="loans" action="write">
                          <button
                            disabled={actingOn === l.id}
                            onClick={() => rejectLoan(l.id)}
                            className="text-red-700 hover:text-red-900 disabled:opacity-50">
                            Rechazar
                          </button>
                        </PermissionGate>
                      </>
                    )}
                    {['ACTIVE', 'APPROVED', 'DEFAULTED', 'LEGAL'].includes(l.status) && !l.earlyPayoffAt && (
                      <PermissionGate module="loans" action="write">
                        <button
                          disabled={actingOn === l.id}
                          onClick={() => openPayoff(l.id)}
                          className="text-blue-700 hover:text-blue-900 disabled:opacity-50">
                          Cancelación anticipada
                        </button>
                      </PermissionGate>
                    )}
                    {l.earlyPayoffAt && l.status === 'COMPLETED' && (
                      <PermissionGate criticalPoint="reverse_payment">
                        <button
                          disabled={actingOn === l.id}
                          onClick={() => reversePayoff(l.id)}
                          className="text-orange-700 hover:text-orange-900 disabled:opacity-50">
                          Revertir cancelación
                        </button>
                      </PermissionGate>
                    )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
        <div>
          Página {meta.page} de {meta.pageCount} · {meta.total.toLocaleString('es-AR')} resultados
        </div>
        <div className="flex items-center gap-2">
          <select
            value={filters.pageSize}
            onChange={(e) => setFilters((f) => ({ ...f, pageSize: Number(e.target.value), page: 1 }))}
            className="px-2 py-1 border rounded-md"
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <button
            type="button"
            disabled={filters.page <= 1 || loading}
            onClick={() => setFilters((f) => ({ ...f, page: f.page - 1 }))}
            className="px-3 py-1 border rounded-md disabled:opacity-40"
          >
            Anterior
          </button>
          <button
            type="button"
            disabled={filters.page >= meta.pageCount || loading}
            onClick={() => setFilters((f) => ({ ...f, page: f.page + 1 }))}
            className="px-3 py-1 border rounded-md disabled:opacity-40"
          >
            Siguiente
          </button>
        </div>
      </div>

      {approveTarget && (
        <DisbursementApproveModal
          loan={approveTarget}
          onClose={() => setApproveTarget(null)}
          onApproved={async () => {
            setApproveTarget(null);
            await load();
          }}
          onError={(message) => setError(message)}
        />
      )}

      {transferTarget && (
        <CompleteBankDisbursementModal
          loan={transferTarget}
          onClose={() => setTransferTarget(null)}
          onCompleted={async () => {
            setTransferTarget(null);
            await load();
          }}
          onError={(message) => setError(message)}
        />
      )}

      {payoffLoanId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4">Cancelación anticipada</h3>
            {payoffLoading && !payoffQuote ? (
              <p className="text-gray-500 text-sm">Calculando monto…</p>
            ) : payoffQuote ? (
              <div className="space-y-2 text-sm mb-6">
                <div className="flex justify-between"><span>Capital</span><span>${payoffQuote.principal.toLocaleString()}</span></div>
                <div className="flex justify-between"><span>Intereses</span><span>${payoffQuote.interest.toLocaleString()}</span></div>
                <div className="flex justify-between"><span>Gastos</span><span>${Number(payoffQuote.fees ?? 0).toLocaleString()}</span></div>
                <div className="flex justify-between"><span>Punitorios</span><span>${Number(payoffQuote.lateFees ?? payoffQuote.penalties ?? 0).toLocaleString()}</span></div>
                <div className="flex justify-between font-semibold border-t pt-2"><span>Total</span><span>${payoffQuote.total.toLocaleString()}</span></div>
              </div>
            ) : null}
            <div className="flex justify-end gap-2">
              <button onClick={() => { setPayoffLoanId(null); setPayoffQuote(null); }} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md">Cancelar</button>
              <button onClick={confirmPayoff} disabled={payoffLoading || !payoffQuote} className="px-4 py-2 bg-blue-600 text-white rounded-md disabled:opacity-50">
                {payoffLoading ? 'Procesando…' : 'Confirmar cancelación'}
              </button>
            </div>
          </div>
        </div>
      )}

      {cancelTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-2">Anular préstamo</h3>
            <p className="text-sm text-gray-500 mb-4">
              {cancelTarget.customer?.firstName} {cancelTarget.customer?.lastName} — ${cancelTarget.amount.toLocaleString()} ({cancelTarget.status})
            </p>
            {['APPROVED', 'ACTIVE', 'DEFAULTED', 'LEGAL'].includes(cancelTarget.status) && (
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3 mb-4">
                Se revertirá el asiento de desembolso. Si hay cobros registrados, anulalos primero desde Pagos.
              </p>
            )}
            <label className="block text-sm font-medium text-gray-700 mb-1">Motivo (mín. 5 caracteres)</label>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md mb-4"
              placeholder="Ej: error de carga, solicitud del cliente…"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => { setCancelTarget(null); setCancelReason(''); }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md">
                Cerrar
              </button>
              <button
                onClick={confirmCancelLoan}
                disabled={cancelling || cancelReason.trim().length < 5}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50">
                {cancelling ? 'Anulando…' : 'Confirmar anulación'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Componente de Layout
function Layout({ children }: { children: React.ReactNode }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [iconCollapsed, setIconCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_ICON_COLLAPSE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const { can, loading, session } = usePermissions();
  const location = useLocation();
  const fullBleed = location.pathname === '/applications/new';

  // En nueva presolicitud: menú oculto para ver toda la pantalla.
  useEffect(() => {
    if (fullBleed) setSidebarOpen(false);
  }, [fullBleed]);

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_ICON_COLLAPSE_KEY, iconCollapsed ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [iconCollapsed]);

  const displayName = session
    ? `${session.user.firstName} ${session.user.lastName}`.trim() || session.user.email
    : 'Usuario';

  const openSidebar = () => {
    setSidebarOpen(true);
    if (iconCollapsed) setIconCollapsed(false);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const mainPad = fullBleed ? 'lg:pl-0' : iconCollapsed ? 'lg:pl-16' : 'lg:pl-64';

  return (
          <div className="min-h-screen bg-gray-50">
      <AppSidebar
        can={can}
        loading={loading}
        mobileOpen={sidebarOpen}
        onMobileClose={closeSidebar}
        forceHidden={fullBleed && !sidebarOpen}
        iconCollapsed={iconCollapsed}
        onIconCollapsedChange={setIconCollapsed}
      />

      {/* Main content */}
      <div className={mainPad}>
        {/* Top bar */}
        <div className="sticky top-0 z-40 border-b border-gray-200 bg-white shadow-sm">
          <div className="flex min-h-14 items-center gap-3 px-4 py-2 sm:gap-4 sm:px-6 lg:px-8">
            <button
              type="button"
              className={`-ml-1 rounded-md p-2 text-gray-600 hover:bg-gray-100 hover:text-gray-900 ${
                fullBleed || iconCollapsed ? '' : 'lg:hidden'
              }`}
              onClick={openSidebar}
              title="Abrir menú"
            >
              <span className="sr-only">Abrir sidebar</span>
              <span className="text-lg leading-none">☰</span>
            </button>

            <div className="flex min-w-0 flex-1 items-center gap-3">
              <TenantContextBar />
              <div className="hidden h-6 w-px shrink-0 bg-gray-200 md:block" aria-hidden />
              <div className="min-w-0 flex-1">
                <CashSessionBar />
              </div>
            </div>

            <div className="flex shrink-0 items-center gap-3 sm:gap-4">
              <button
                type="button"
                className="rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                title="Notificaciones"
                onClick={() => {
                  window.location.href = '/notifications';
                }}
              >
                <span className="sr-only">Notificaciones</span>
                <span className="text-base leading-none">🔔</span>
              </button>
              <div className="hidden h-6 w-px bg-gray-200 lg:block" aria-hidden />
              <div className="flex items-center gap-2">
                <span className="hidden text-sm font-medium text-gray-800 sm:inline">{displayName}</span>
                <button
                  type="button"
                  className="rounded-md px-2 py-1 text-sm text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                  onClick={async () => {
                    await apiService.logout();
                    window.location.href = '/';
                  }}
                >
                  Cerrar Sesión
                </button>
              </div>
            </div>
          </div>
          <CashSessionStaleBanner />
        </div>

        {/* Page content */}
        <main className={fullBleed ? 'py-0' : 'py-6'}>
          {children}
        </main>
      </div>

      {/* Overlay drawer */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 lg:hidden"
          onClick={closeSidebar}
        />
      )}
    </div>
  );
}

// Componente principal
export default function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(
    Boolean(localStorage.getItem('authToken')),
  );

  React.useEffect(() => {
    const checkAuth = () => {
      setIsAuthenticated(Boolean(localStorage.getItem('authToken')));
    };

    checkAuth();
    window.addEventListener('storage', checkAuth);
    return () => window.removeEventListener('storage', checkAuth);
  }, []);

  if (!isAuthenticated) {
    return <Login />;
  }

  return (
    <PermissionsProvider>
      <Layout>
        <Routes>
          <Route path="/forbidden" element={<Forbidden />} />
          <Route
            path="/"
            element={
              <HomeRedirect>
                <Dashboard />
              </HomeRedirect>
            }
          />
          <Route path="/customers" element={<ProtectedRoute module="customers"><Customers /></ProtectedRoute>} />
          <Route path="/customers/:id" element={<ProtectedRoute module="customers"><CustomerDetail /></ProtectedRoute>} />
          <Route path="/products" element={<ProtectedRoute module="loan-products"><LoanProducts /></ProtectedRoute>} />
          <Route path="/reference-data" element={<ProtectedRoute module="reference-data"><ReferenceData /></ProtectedRoute>} />
          <Route path="/applications/new" element={<ProtectedRoute module="applications"><NewLoanApplication /></ProtectedRoute>} />
          <Route path="/applications" element={<ProtectedRoute module="applications"><LoanApplications /></ProtectedRoute>} />
          <Route path="/loans/pending" element={<ProtectedRoute module="loans-pending"><Loans /></ProtectedRoute>} />
          <Route path="/loans/transfer-pending" element={<ProtectedRoute module="loans-transfer-pending"><Loans /></ProtectedRoute>} />
          <Route path="/loans/renewals" element={<ProtectedRoute module="loans-renewals"><LoanRenewals /></ProtectedRoute>} />
          <Route path="/loans/:id" element={<ProtectedRoute module="loans"><LoanDetail /></ProtectedRoute>} />
          <Route path="/loans" element={<ProtectedRoute module="loans"><Loans /></ProtectedRoute>} />
          <Route path="/objectives" element={<ProtectedRoute module="objectives"><BranchObjectives /></ProtectedRoute>} />
          <Route path="/payments" element={<ProtectedRoute module="payments"><Payments /></ProtectedRoute>} />
          <Route path="/payments/inquiry" element={<Navigate to="/payments" replace />} />
          <Route path="/payroll-discounts" element={<ProtectedRoute module="payroll-discounts"><PayrollDiscounts /></ProtectedRoute>} />
          <Route path="/bapro-debit" element={<ProtectedRoute module="payments"><BaproDebitBatches /></ProtectedRoute>} />
          <Route path="/payroll-batch-collections" element={<ProtectedRoute module="payroll-batch"><PayrollBatchCollections /></ProtectedRoute>} />
          <Route path="/collections" element={<ProtectedRoute module="collections-desk"><Collections /></ProtectedRoute>} />
          <Route path="/branches" element={<ProtectedRoute module="branches"><BranchesAndCash /></ProtectedRoute>} />
          <Route
            path="/treasury"
            element={
              <ProtectedRoute module="cash-mine">
                <TreasuryLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="position" replace />} />
            <Route path="position" element={<TreasuryPage />} />
            <Route path="move" element={<MoveFundsWizard />} />
            <Route path="transfers" element={<TreasuryPage />} />
            <Route path="approvals" element={<TreasuryPage />} />
            <Route path="banks" element={<TreasuryBanksPage />} />
            <Route path="supervision" element={<TreasurySupervisionPage />} />
            <Route path="cash-registers" element={<TreasuryCashRegistersPage />} />
            <Route path="config" element={<TreasuryPage />} />
          </Route>
          <Route path="/cash" element={<ProtectedRoute module="cash-mine"><CashRegistersPage /></ProtectedRoute>} />
          <Route
            path="/cash/move"
            element={
              <ProtectedRoute module="cash-mine">
                <div className="p-6">
                  <MoveFundsWizard />
                </div>
              </ProtectedRoute>
            }
          />
          <Route path="/bank-reconciliation" element={<ProtectedRoute module="bank-reconciliation"><BankReconciliation /></ProtectedRoute>} />
          <Route path="/payables" element={<ProtectedRoute module="payables"><PayablesPage /></ProtectedRoute>} />
          <Route path="/compliance" element={<ProtectedRoute module="compliance"><CustomerCompliance /></ProtectedRoute>} />
          {FEATURES.kyc ? (
            <Route path="/kyc" element={<ProtectedRoute module="kyc"><KYC /></ProtectedRoute>} />
          ) : null}
          <Route
            path="/accounting"
            element={
              <ProtectedRoute module="accounting">
                <AccountingLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<AccountingIndexPage />} />
            <Route path="books/journal" element={<AccountingViewPage view="journal" />} />
            <Route path="books/ledger" element={<AccountingViewPage view="ledger" />} />
            <Route path="books/trial" element={<AccountingViewPage view="trial" />} />
            <Route path="statements/income" element={<AccountingViewPage view="income" />} />
            <Route path="statements/balance" element={<AccountingViewPage view="balance" />} />
            <Route path="statements/cashflow" element={<AccountingViewPage view="cashflow" />} />
            <Route path="close" element={<AccountingViewPage view="closeWizard" />} />
            <Route path="close/periods" element={<AccountingViewPage view="periods" />} />
            <Route path="close/fiscal-years" element={<AccountingViewPage view="fiscalYears" />} />
            <Route path="close/working-papers" element={<AccountingViewPage view="workingPapers" />} />
            <Route path="close/integrity" element={<AccountingViewPage view="integrity" />} />
            <Route path="masters/chart" element={<ProtectedRoute module="accounting" action="write"><AccountingViewPage view="chart" /></ProtectedRoute>} />
            <Route path="masters/cost-centers" element={<ProtectedRoute module="accounting" action="write"><AccountingViewPage view="costCenters" /></ProtectedRoute>} />
            <Route path="masters/business-units" element={<ProtectedRoute module="accounting" action="write"><AccountingViewPage view="businessUnits" /></ProtectedRoute>} />
            <Route path="masters/mappings" element={<ProtectedRoute module="accounting" action="write"><AccountingViewPage view="mappings" /></ProtectedRoute>} />
            <Route path="masters" element={<Navigate to="/accounting/masters/chart" replace />} />
            <Route path="tools/imports" element={<AccountingViewPage view="imports" />} />
            <Route path="tools/fx" element={<AccountingViewPage view="multicurrency" />} />
            <Route path="tools/inflation" element={<AccountingViewPage view="inflation" />} />
          </Route>
          <Route path="/fiscal" element={<ProtectedRoute module="fiscal"><FiscalLegal /></ProtectedRoute>} />
          <Route path="/crm" element={<ProtectedRoute module="crm"><CrmMarketing /></ProtectedRoute>} />
          <Route path="/audit" element={<ProtectedRoute module="audit"><AuditLogs /></ProtectedRoute>} />
          <Route path="/risk" element={<ProtectedRoute module="risk"><RiskManagement /></ProtectedRoute>} />
          <Route path="/notifications" element={<ProtectedRoute module="notifications"><Notifications /></ProtectedRoute>} />
          <Route path="/analytics" element={<ProtectedRoute module="analytics"><Analytics /></ProtectedRoute>} />
          <Route path="/reports" element={<ProtectedRoute module="reports"><Reports /></ProtectedRoute>} />
          <Route path="/reports/operational" element={<ProtectedRoute module="reports-operational"><OperationalReports /></ProtectedRoute>} />
          <Route path="/reports/legacy" element={<ProtectedRoute module="reports-legacy"><LegacyReports /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute module="settings"><Settings /></ProtectedRoute>} />
          <Route path="/users" element={<ProtectedRoute module="users"><Users /></ProtectedRoute>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Layout>
    </PermissionsProvider>
  );
}
